import axios, { AxiosError } from "axios"
import { ElMessage } from "element-plus"
import { clearBrowserSessionData } from "@/utils/clientSession"

function syncPersistedToken(token: string) {
  const persistedState = localStorage.getItem("my-app-store")

  if (!persistedState) {
    return
  }

  try {
    const parsedState = JSON.parse(persistedState)
    if (parsedState?.Users) {
      parsedState.Users.token = token
      localStorage.setItem("my-app-store", JSON.stringify(parsedState))
    }
  } catch (error) {
    void error
  }
}

/**
 * =========================
 * 后端错误返回类型定义
 * =========================
 */
interface ApiErrorDetail {
  message?: string
  path?: string
}

interface ApiErrorResponse {
  detail?: ApiErrorDetail
}

// 防止多个接口同时 401 导致多次跳转
let isRedirecting = false


/**
 * =========================
 * axios 实例
 * =========================
 */
const http = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL,
  timeout: 50000,
})

/**
 * =========================
 * 请求拦截器
 * =========================
 */
http.interceptors.request.use((config) => {
    const token = localStorage.getItem("token")
    if (token) {
      config.headers = config.headers || {}
      config.headers.Authorization = token.startsWith("Bearer ")
        ? token
        : `Bearer ${token}`
      config.headers["X-Page-Path"] = `${window.location.pathname}${window.location.search}`
      config.headers["X-Page-Name"] = document.title || ""
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * =========================
 * 响应拦截器
 * =========================
 */
http.interceptors.response.use(
  (response) => {
    // 从响应头中读取新 token
    const newToken = response.headers?.authorization
    const currentToken = localStorage.getItem("token")
    const requestAuthorization = response.config.headers?.Authorization
    const sentToken = typeof requestAuthorization === "string"
      ? requestAuthorization.replace(/^Bearer\s+/i, "")
      : ""

    if (newToken && currentToken && sentToken === currentToken) {
      localStorage.setItem("token", newToken)
      syncPersistedToken(newToken)
    }

    return response
  },
  (error: AxiosError) => {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status

      const data = error.response?.data as ApiErrorResponse | undefined
      const detail = data?.detail

      /**
       * -------- 401 统一处理 --------
       */
      if (status === 401 && detail) {

        if (!isRedirecting) {
          isRedirecting = true

          ElMessage({
            message: detail.message || "登录已失效，请重新登录",
            type: "error",
            showClose: true,
          })
          // 跳转登录页
          if (detail?.path) {
            const redirectPath = detail.path
          
            // 清理登录态（不要触发 router）
            clearBrowserSessionData()
          
            // 强制整页跳转（绕开 Vue）
            setTimeout(() => {
              window.location.replace(redirectPath)
            }, 1000)
          }

          setTimeout(() => {
            isRedirecting = false
          }, 1000)
        }

        return Promise.reject(error)
      }

      /**
       * -------- 其他错误 --------
       */
      ElMessage({
        message:
          detail?.message ||
          error.message ||
          "请求失败，请稍后重试",
        type: "error",
        showClose: true,
      })
    }

    return Promise.reject(error)
  }
)

export default http
